import React, { useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  writeBatch,
  serverTimestamp,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { firestore, GeoFirestore } from "../../../../firebase";
import firebase from "firebase/compat/app";
import ngeohash from "ngeohash";
import DisplayEventsFromXml from "./displayEventsFromXml";
import { generateUniqueSlug } from "../../../../utils/slug";

const db = firestore;

const GetEventsFromXml = ({ currentBusinessID, businessLocations }) => {
  const [xmlUrl, setXmlUrl] = useState("");
  const [eventsData, setEventsData] = useState([]);

  const [eventLocation, setEventLocation] = useState(null);
  const [dataConfig, setDataConfig] = useState({
    title: "",
    description: "",
    age: "",
    date: "",
    ticketUrl: "",
    mainImage: "",
    price: "",
    location: "",
  });

  const getValueByPath = (obj, path) => {
    if (!obj || !path) return "";

    return path.split(".").reduce((acc, key) => {
      if (acc == null) return undefined;

      const arrayMatch = key.match(/^(\w+)\[(\d+)\]$/);
      if (arrayMatch) {
        const [, prop, index] = arrayMatch;
        return acc[prop]?.[Number(index)];
      }

      return acc[key];
    }, obj);
  };

  const fetchXmlEvents = async () => {
    try {
      const response = await fetch(
        "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/fetch-events-from-xml",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: xmlUrl }),
        },
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch XML");
      }

      const parsedData = result.data;
      console.log("Parsed XML Data:", parsedData);

      const items = getValueByPath(parsedData, "rss.channel.item") || [];

      const formattedEvents = items.map((item) => ({
        title: getValueByPath(item, dataConfig.title),
        description: getValueByPath(item, dataConfig.description),
        age: getValueByPath(item, dataConfig.age),
        date: getValueByPath(item, dataConfig.date),
        ticketUrl: getValueByPath(item, dataConfig.ticketUrl),
        mainImage: getValueByPath(item, dataConfig.mainImage),
        price: getValueByPath(item, dataConfig.price),
        location: getValueByPath(item, dataConfig.location),
      }));

      setEventsData(formattedEvents);
      console.log(formattedEvents);
    } catch (error) {
      console.error("Error fetching XML data:", error);
    }
  };

  const TARGET_LANG = "en-US";

  const translateText = async (text) => {
    try {
      const res = await fetch(
        "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/translateWithDeepL",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text,
            toLang: TARGET_LANG,
            fromLang: "nb_NO",
          }),
        },
      );

      const json = await res.json();
      return json.text ?? text;
    } catch (err) {
      console.error("Translate error:", err);
      return text;
    }
  };

  const onSaveEvents = async () => {
    try {
      const geocollection = GeoFirestore.collection("events");
      const userInfoRef = doc(db, "businesses", currentBusinessID);

      const userInformation = await getDoc(userInfoRef);

      if (!userInformation.exists()) {
        throw new Error("Business not found");
      }

      const userData = userInformation.data();

      const englishDescriptions = await Promise.all(
        eventsData.map((event) => translateText(event.description)),
      );

      const events = [];

      // generateUniqueSlug only checks Firestore, so it can't see the other
      // not-yet-written events in this same import batch — track those
      // locally too, or two same-named events in one XML feed would both
      // land on the same slug (Promise.all below writes them in parallel).
      const usedSlugsThisBatch = new Set();
      async function claimSlug(name) {
        let candidate = await generateUniqueSlug(name);
        while (usedSlugsThisBatch.has(candidate)) {
          const m = candidate.match(/^(.*)-(\d+)$/);
          const base = m ? m[1] : candidate;
          const n = m ? parseInt(m[2], 10) + 1 : 2;
          candidate = `${base}-${n}`;
        }
        usedSlugsThisBatch.add(candidate);
        return candidate;
      }

      for (const [index, event] of eventsData.entries()) {
        const eventDateObject = new Date(event.date);

        const startDateMs = new Date(eventDateObject).setHours(0, 0, 0, 0);

        const startDate = Math.floor(startDateMs / 1000);
        const endDate = Math.floor((startDateMs + 86400000) / 1000);
        const eventDate = startDate;

        const eventDateTimeTimestamp = Math.floor(
          eventDateObject.getTime() / 1000,
        );

        const dateText = eventDateObject.toLocaleDateString("nb-NO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });

        const timeText = eventDateObject.toLocaleTimeString("nb-NO", {
          hour: "2-digit",
          minute: "2-digit",
        });

        const ticketURL = event.ticketUrl || "";
        const location = event.location || "";
        const title = event.title || "";
        const lang = "nb_NO";
        const currency = "NOK";
        const englishDescription =
          englishDescriptions[index] || event.description || "";

        events.push({
          aboutBusiness: event.description || "",
          aboutBusinessLang: {
            en_US: englishDescription,
            nb_NO: event.description || "",
          },
          aboutEvent:
            "Dato: " +
            dateText +
            "\nTid: " +
            timeText +
            "\nBilletter fås kjøpt her:\n" +
            ticketURL,
          aboutEventLang: {
            nb_NO:
              "Dato: " +
              dateText +
              "\nTid: " +
              timeText +
              "\nBilletter fås kjøpt her:\n" +
              ticketURL,
            en_US:
              "Date: " +
              dateText +
              "\nTime: " +
              timeText +
              "\nTickets can be purchased here:\n" +
              ticketURL,
          },
          active: true,
          averagePrice: null,
          currency: currency,
          endDate: endDate,
          eventDate: eventDate,
          eventTime: timeText,
          eventDateTime: eventDateTimeTimestamp,
          eventOrganiser: eventLocation.id,
          eventOrganiserName: eventLocation.data().locationName,
          lat: eventLocation.data().lat,
          lng: eventLocation.data().lng,
          coordinates: new firebase.firestore.GeoPoint(
            eventLocation.data().lat,
            eventLocation.data().lng,
          ),
          imageResizeMode: "cover",
          lang: lang,
          locationName: title,
          slug: await claimSlug(title),
          locationTags: [location],
          locationTagsLang: {
            [lang]: [location],
            en_US: [location],
          },
          mainProfileImage: event.mainImage || "",
          paymentPrice: 0,
          shownOnBusinessPage: true,
          startDate: 0,
          tags: ["Studentarrangement"],
          onlyStudents: true,
          tagsLang: {
            [lang]: ["Studentarrangement"],
            en_US: ["Studentevent"],
          },
          type: "event",
        });
      }

      const createdDocs = await Promise.all(
        events.map(async (eventObj) => {
          const docRef = await geocollection.add(eventObj);
          return docRef.id;
        }),
      );

      const existingBusinessEvents = Array.isArray(userData?.events)
        ? userData.events
        : [];

      const businessEvents = [...existingBusinessEvents, ...createdDocs];

      await updateDoc(userInfoRef, {
        events: businessEvents,
        eventUrl: xmlUrl,
        type: "xml",
        xmlDataConfig: dataConfig,
        lastTmSyncAt: serverTimestamp(),
      });

      console.log("XML events synced");

      window.location.reload();
    } catch (error) {
      console.error("Error saving XML events:", error);
    }
  };

  const deleteStudentEvents = async () => {
    try {
      const eventsRef = collection(db, "events");

      const q = query(eventsRef, where("onlyStudents", "==", true));
      const snapshot = await getDocs(q);

      const deletions = snapshot.docs.map((document) =>
        deleteDoc(doc(db, "events", document.id)),
      );

      await Promise.all(deletions);

      console.log(`Deleted ${snapshot.size} student events`);
    } catch (error) {
      console.error("Error deleting student events:", error);
    }
  };

  return eventLocation == null ? (
    businessLocations.length > 0 ? (
      businessLocations.map((lokasjonsInformasjon, rowIndex) => {
        return (
          <div key={rowIndex} className='box' style={{ marginBottom: "15px" }}>
            <div className='businessOverView'>
              <label className='locationNameLabel'>
                {lokasjonsInformasjon.data()?.locationName}
              </label>
              <label>
                {lokasjonsInformasjon.data()?.adress
                  ? lokasjonsInformasjon.data()?.adress
                  : "-"}
              </label>
              <div>
                <a
                  onClick={() => {
                    setEventLocation(lokasjonsInformasjon);
                  }}
                  className='universalButtonRed'
                  style={{ marginLeft: "5px" }}>
                  Velg
                </a>
              </div>
            </div>
          </div>
        );
      })
    ) : (
      <p style={{ textAlign: "center", marginTop: 0 }}>
        Ingen lokasjoner å vise til
      </p>
    )
  ) : (
    <div>
      <h2>Hent inn event data fra en XML feed</h2>
      <input
        placeholder='XML URL'
        value={xmlUrl}
        onChange={(x) => {
          setXmlUrl(x.target.value);
        }}
      />
      <label>Title</label>
      <input
        placeholder='Title'
        value={dataConfig.title}
        onChange={(x) => {
          setDataConfig({ ...dataConfig, title: x.target.value });
        }}
      />
      <label>Description</label>
      <input
        placeholder='Description'
        value={dataConfig.description}
        onChange={(x) => {
          setDataConfig({ ...dataConfig, description: x.target.value });
        }}
      />
      <label>Age</label>
      <input
        placeholder='Age'
        value={dataConfig.age}
        onChange={(x) => {
          setDataConfig({ ...dataConfig, age: x.target.value });
        }}
      />
      <label>Date</label>
      <input
        placeholder='Date'
        value={dataConfig.date}
        onChange={(x) => {
          setDataConfig({ ...dataConfig, date: x.target.value });
        }}
      />
      <label>Ticket URL</label>
      <input
        placeholder='Ticket URL'
        value={dataConfig.ticketUrl}
        onChange={(x) => {
          setDataConfig({ ...dataConfig, ticketUrl: x.target.value });
        }}
      />
      <label>Main Image URL</label>
      <input
        placeholder='Main Image URL'
        value={dataConfig.mainImage}
        onChange={(x) => {
          setDataConfig({ ...dataConfig, mainImage: x.target.value });
        }}
      />
      <label>Price</label>
      <input
        placeholder='Price'
        value={dataConfig.price}
        onChange={(x) => {
          setDataConfig({ ...dataConfig, price: x.target.value });
        }}
      />
      <label>Location</label>
      <input
        placeholder='Location'
        value={dataConfig.location}
        onChange={(x) => {
          setDataConfig({ ...dataConfig, location: x.target.value });
        }}
      />
      <button onClick={fetchXmlEvents}>Fetch Events</button>
      <button onClick={onSaveEvents}>Lagre Events som studentevents</button>

      {eventsData.map((event, index) => (
        <DisplayEventsFromXml
          key={index}
          title={event.title}
          description={event.description}
          age={event.age}
          date={event.date}
          ticketUrl={event.ticketUrl}
          mainImage={event.mainImage}
          price={event.price}
          location={event.location}
        />
      ))}
    </div>
  );
};

export default GetEventsFromXml;
